import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cguWriteErrorMessage } from '@/api/legal-admin-client';
import {
  useCguVersion,
  useDeleteCguDraft,
  useDownloadCguVersionPdf,
  useEditCguDraft,
  usePublishCguVersion,
} from '@/api/use-legal-admin';
import { CguEditor } from '@/routes/admin/cgu/cgu-editor';
import { CguPublishGate } from '@/routes/admin/cgu/cgu-publish-gate';
import {
  formatDate,
  formatDateTime,
  formatOpaqueId,
  stateBadgeVariant,
  stateLabel,
} from '@/routes/admin/cgu/cgu-format';
import { triggerPdfDownload } from '@/routes/admin/cgu/cgu-pdf-download';

// =============================================================================
// cgu-version-detail.tsx — Détail + éditeur d'UNE version CGU (A-CGU ÉTAPE 2, onglet b).
// =============================================================================
// Lit GET /admin/legal/cgu/versions/:id (contenu HTML + métadonnées). Rend l'éditeur MAISON
// (aperçu isolé) en mode ÉDITION pour un Brouillon, en LECTURE SEULE sinon. Actions :
//   - Enregistrer (Brouillon UNIQUEMENT) : PATCH /admin/legal/cgu/versions/:id (editDraft) — le corps
//     `contentHtml` part au back (ca0aa8b) qui le NETTOIE et le persiste. 409 si non-Brouillon,
//     400 si HTML aberrant (message générique, jamais serveur brut). Succès → invalidation + refetch ;
//   - Publier (Brouillon) : POST publish + confirmation (le back refuse un corps vide) ;
//   - Supprimer (Brouillon UNIQUEMENT) : DELETE + confirmation, erreur back relayée telle quelle ;
//   - Télécharger le PDF (SSI la version porte un PDF : Publiée/Archivée). Brouillon → pas de bouton.
//
// L'éditeur d'un Brouillon est PLEINEMENT ÉDITANT (le contenu est persisté par le back). Une version
// Publiée/Archivée est OPPOSABLE et IMMUABLE : éditeur en lecture seule (disabled), pas de bouton
// Enregistrer. La CRÉATION d'un nouveau brouillon passe par l'onglet quand aucune version n'est
// sélectionnée. Le front ne sanitise jamais : l'aperçu reste dans l'iframe sandbox="" de CguEditor.

export interface CguVersionDetailProps {
  readonly cguVersionId: string;
  /** Notifie le parent après suppression (désélection de la version supprimée). */
  readonly onDeleted: () => void;
}

export function CguVersionDetail({
  cguVersionId,
  onDeleted,
}: CguVersionDetailProps): React.JSX.Element {
  const detail = useCguVersion(cguVersionId);
  const publish = usePublishCguVersion();
  const remove = useDeleteCguDraft();
  const edit = useEditCguDraft();
  const downloadPdf = useDownloadCguVersionPdf();

  const [confirmPublish, setConfirmPublish] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  // Corps HTML en cours d'édition. `null` = non modifié depuis le dernier (re)chargement serveur —
  // l'éditeur reflète alors `contentHtml`. À l'enregistrement réussi, on remet `null` pour resynchro-
  // niser sur le contenu NETTOYÉ renvoyé par le back (le sanitiseur peut transformer le HTML saisi).
  const [draftBody, setDraftBody] = React.useState<string | null>(null);

  const contentHtml = detail.data?.contentHtml ?? '';
  const editableBody = draftBody ?? contentHtml;

  // --- Autosave silencieux (C-08, DBU l.2038) : débounce sur frappe + flush on perte de focus.
  //     Réutilise editDraft (PATCH). Silencieux : aucun toast/bouton ; l'erreur existante remonte.
  //     PAS d'audit dédié (doctrine wave A0-MINEURS f). Ne PATCH jamais un contenu identique/serveur.
  const AUTOSAVE_DEBOUNCE_MS = 1500;
  const isDraft = detail.data?.state === 'Brouillon';
  const editMutate = edit.mutate;
  const autosaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushAutosave = React.useCallback((): void => {
    if (autosaveTimer.current !== null) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    if (!isDraft || draftBody === null || draftBody === contentHtml) {
      return;
    }
    editMutate(
      { cguVersionId, input: { contentHtml: draftBody } },
      { onSuccess: () => setDraftBody(null) },
    );
  }, [isDraft, draftBody, contentHtml, editMutate, cguVersionId]);

  React.useEffect(() => {
    if (!isDraft || draftBody === null || draftBody === contentHtml) {
      return;
    }
    autosaveTimer.current = setTimeout(() => {
      editMutate(
        { cguVersionId, input: { contentHtml: draftBody } },
        { onSuccess: () => setDraftBody(null) },
      );
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current !== null) {
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = null;
      }
    };
  }, [isDraft, draftBody, contentHtml, editMutate, cguVersionId]);

  if (detail.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement de la version…
      </p>
    );
  }
  if (detail.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {detail.error.message}
      </p>
    );
  }

  const version = detail.data;

  const onConfirmPublish = (): void => {
    publish.mutate(cguVersionId, {
      onSettled: () => setConfirmPublish(false),
    });
  };

  const onConfirmDelete = (): void => {
    remove.mutate(cguVersionId, {
      onSuccess: () => {
        setConfirmDelete(false);
        onDeleted();
      },
      onError: () => setConfirmDelete(false),
    });
  };

  const onDownload = (): void => {
    downloadPdf.mutate(cguVersionId, {
      onSuccess: (bytes) => {
        triggerPdfDownload(bytes, `${version.versionNumber}.pdf`);
      },
    });
  };

  const onSave = (): void => {
    // Garde de présentation : SEUL un Brouillon est éditable (le back refuse sinon, 409).
    if (!isDraft) {
      return;
    }
    edit.mutate(
      { cguVersionId, input: { contentHtml: editableBody } },
      // Succès → on relâche l'état local pour resynchroniser sur le contenu nettoyé (refetch détail).
      { onSuccess: () => setDraftBody(null) },
    );
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">
            {version.versionNumber}
          </h2>
          <Badge variant={stateBadgeVariant(version.state)}>
            {stateLabel(version.state)}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {version.hasPdf ? (
            <Button
              type="button"
              variant="outline"
              onClick={onDownload}
              disabled={downloadPdf.isPending}
            >
              {downloadPdf.isPending ? 'Téléchargement…' : 'Télécharger le PDF'}
            </Button>
          ) : null}
          {isDraft ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onSave}
                disabled={edit.isPending}
              >
                {edit.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              <Button
                type="button"
                onClick={() => setConfirmPublish(true)}
                disabled={publish.isPending}
              >
                Publier
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={remove.isPending}
              >
                Supprimer le brouillon
              </Button>
            </>
          ) : null}
        </div>
      </header>

      {edit.isError ? (
        <p role="alert" className="text-destructive">
          Enregistrement refusé : {cguWriteErrorMessage(edit.error, 'erreur inconnue.')}
        </p>
      ) : null}
      {edit.isSuccess ? (
        <p role="status" className="text-muted-foreground">
          Modifications enregistrées.
        </p>
      ) : null}
      {publish.isError ? (
        <p role="alert" className="text-destructive">
          Publication refusée : {publish.error.message}
        </p>
      ) : null}
      {remove.isError ? (
        <p role="alert" className="text-destructive">
          Suppression refusée : {remove.error.message}
        </p>
      ) : null}
      {downloadPdf.isError ? (
        <p role="alert" className="text-destructive">
          Téléchargement du PDF impossible : {downloadPdf.error.message}
        </p>
      ) : null}

      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
        <MetaField label="Entrée en vigueur" value={formatDate(version.effectiveDate)} />
        <MetaField label="Publiée le" value={formatDateTime(version.publishedAt)} />
        <MetaField
          label="Publiée par"
          value={formatOpaqueId(version.publishedBy)}
          opaque
        />
        <MetaField label="Créée le" value={formatDateTime(version.createdAt)} />
        <MetaField
          label="Créée par"
          value={formatOpaqueId(version.createdBy)}
          opaque
        />
        <MetaField label="Archivée le" value={formatDateTime(version.archivedAt)} />
        <MetaField
          label="Dernière modification"
          value={formatDateTime(version.lastModifiedAt)}
        />
        <MetaField label="Signatures collectées" value={String(version.nbSignatures)} />
      </dl>

      <section aria-label="Contenu de la version" className="space-y-3" onBlur={flushAutosave}>
        <CguEditor
          value={editableBody}
          onChange={setDraftBody}
          disabled={!isDraft}
          id={`cgu-editor-${cguVersionId}`}
        />
      </section>

      <CguPublishGate
        open={confirmPublish}
        versionNumber={version.versionNumber}
        contentHtml={contentHtml}
        publishing={publish.isPending}
        onConfirm={onConfirmPublish}
        onCancel={() => setConfirmPublish(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Supprimer ce brouillon ?"
        description={`Le brouillon « ${version.versionNumber} » sera définitivement effacé. Cette action est irréversible.`}
        confirmLabel={remove.isPending ? 'Suppression…' : 'Supprimer définitivement'}
        confirmDisabled={remove.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function MetaField({
  label,
  value,
  opaque = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly opaque?: boolean;
}): React.JSX.Element {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={opaque ? 'font-mono text-sm' : 'text-sm'}>{value}</dd>
    </div>
  );
}
